import React, { useState, useRef, useEffect } from 'react';
import { Play } from 'lucide-react';

interface LazyYouTubeProps {
  videoId: string;
  title: string;
  className?: string;
  autoplay?: boolean;
  thumbnailQuality?: 'default' | 'hqdefault' | 'maxresdefault';
}

const LazyYouTube: React.FC<LazyYouTubeProps> = ({
  videoId,
  title,
  className = '',
  autoplay = false,
  thumbnailQuality = 'hqdefault'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading thumbnail only
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px' // Load thumbnail earlier for smoother UX
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    setUserInteracted(true);
    // Defer iframe creation to next frame to avoid blocking
    setTimeout(() => {
      setIsLoaded(true);
    }, 0);
  };

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/${thumbnailQuality}.jpg`;
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}${autoplay ? '?autoplay=1&rel=0&modestbranding=1&controls=1&enablejsapi=0&fs=0&iv_load_policy=3&playsinline=1&origin=' + encodeURIComponent(window.location.origin) : '?rel=0&modestbranding=1&controls=1&enablejsapi=0&fs=0&iv_load_policy=3&playsinline=1&origin=' + encodeURIComponent(window.location.origin)}`;

  return (
    <div 
      ref={containerRef}
      className={`relative aspect-video rounded-2xl overflow-hidden border border-amber-500/20 bg-slate-900/50 ${className}`}
    >
      {!isLoaded ? (
        <>
          {/* Thumbnail and Play Button - Only loads when in view */}
          {isInView && (
            <>
              <img
                src={thumbnailUrl}
                alt={title}
                className="w-full h-full object-cover"
                loading="lazy"
                width="480"
                height="360"
              />
              <div className="absolute inset-0 bg-black/20"></div>
              <button
                onClick={handlePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors group"
                aria-label={`Play video: ${title}`}
              >
                <div className="bg-red-600 hover:bg-red-700 rounded-full p-4 group-hover:scale-110 transition-transform shadow-lg">
                  <Play className="text-white ml-1" size={32} fill="currentColor" />
                </div>
              </button>
            </>
          )}
          {!isInView && (
            <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
              <div className="bg-red-600 rounded-full p-4">
                <Play className="text-white ml-1" size={32} fill="currentColor" />
              </div>
            </div>
          )}
        </>
      ) : (
        /* YouTube Iframe - Only loaded after user interaction */
        <iframe
          src={embedUrl}
          title={title}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          style={{ border: 'none' }}
          sandbox="allow-scripts allow-same-origin allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}
    </div>
  );
};

export default LazyYouTube;